import React, { useMemo, useState } from 'react';
import { translate } from '../../Services/translation';
import { DropdownOption } from '../Dropdown';
import ProductCardProps from './types/ProductCardProps';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';
import ProductTag from '../ProductTags/ProductTag';
import VariantSelectBuyButtonContainer from '../VariantSelectBuyButton/VariantSelectBuyButtonContainer';
import LipscoreRating from '../Lipscore/LipscoreRating';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import clsx from 'clsx';

const ProductCardLandscape = ({
  product,
  isVariantListing = false,
  lazyLoadImage,
}: ProductCardProps) => {
  const [selectedOption, setSelectedOption] = useState<DropdownOption>();

  const userInfo = useTypedSelector((state) => state.userInfo);
  const isBusinessCustomer = userInfo?.current?.isBusinessCustomer;

  return (
    <div className="flex bg-white border border-solid border-gray-200 rounded p-3 flex-col mb-1 md:flex-row">
      <div className="flex flex-1 mb-4 md:mb-0">
        <div className="bg-white max-w-[80px] w-full h-full flex items-center justify-center">
          {product.images && product.images.length > 0 && (
            <a href={product.url}>
              <ResponsiveImage
                {...product.images[0]}
                lazyLoad={lazyLoadImage}
                imgClassName="w-full aspect-square object-contain"
              />
            </a>
          )}
        </div>
        <div className="flex-1 my-0 mx-3">
          {product.tags && product.tags.length > 0 ? (
            <div className="flex gap-2 mb-1">
              {product.tags.map((tag) => (
                <ProductTag tagSize="large" id={tag.id} key={tag.id}>
                  {tag.name}
                </ProductTag>
              ))}
              {product.discountPercantage && (
                <ProductTag
                  id="discount"
                  tagType="campaign"
                  tagSize="large"
                >
                  {product.discountPercantage}
                </ProductTag>
              )}
              {product.isNew && (
                <ProductTag id="isNew" tagSize="large">
                  {translate('product-tag.is-new')}
                </ProductTag>
              )}
            </div>
          ) : (
            <>
              {(product.discountPercantage || product.isNew) && (
                <div className="flex gap-2 mb-1">
                  {product.discountPercantage && (
                    <ProductTag
                      id="discount"
                      tagType="campaign"
                      tagSize="large"
                    >
                      {product.discountPercantage}
                    </ProductTag>
                  )}
                  {product.isNew && (
                    <ProductTag id="isNew" tagSize="large">
                      {translate('product-tag.is-new')}
                    </ProductTag>
                  )}
                </div>
              )}
            </>
          )}
          <a
            className="text-md flex font-satoshi-bold mb-1.5"
            href={product.url}
          >
            {product.name}
          </a>
          {product.productCardDescription && (
            <div className="text-md color-grey-500 mb-2">
              {product.productCardDescription}
            </div>
          )}
          <div className="h-[18px] mb-2">
            {product.lipscoreProduct?.productModel && (
              <LipscoreRating
                productModel={product.lipscoreProduct.productModel}
                readOnly={true}
              />
            )}
          </div>
          <div className="flex gap-1 items-end mb-2">
            {product.fromPrice.hasFromPrice ? (
              <span
                className={clsx(
                  product.fromPrice.fromPrice.campaignPrice &&
                    'text-primary-500',
                  'text-md font-satoshi-bold m-0'
                )}
              >
                {translate('product-page.from-product-price')}
              </span>
            ) : (
              ''
            )}
            {product.fromPrice.fromPrice.campaignPrice ? (
              <div className="text-md font-satoshi-bold m-0 text-primary-500">
                {product.fromPrice.fromPrice.formattedCampaignPrice}{' '}
                {isBusinessCustomer &&
                  translate('product-card.without-vat')}{' '}
                <span className="line-through color-gray-600">
                  {product.fromPrice.fromPrice.formattedPrice}{' '}
                </span>
              </div>
            ) : (
              product.fromPrice.fromPrice.price && (
                <div className="text-md font-satoshi-bold m-0">
                  {product.fromPrice.fromPrice.formattedPrice}{' '}
                  {isBusinessCustomer &&
                    translate('product-card.without-vat')}
                </div>
              )
            )}
          </div>

          <div className="text-sm text-gray-600 flex">
            <div className="mr-2 hidden md:block">
              {product.hasVariants ? (
                <span>
                  {product.stockStatus.inStock
                    ? translate('product-listing.is-in-stock')
                    : translate('product-listing.is-not-in-stock')}
                </span>
              ) : (
                product.stockStatus.description
              )}
            </div>
            {product.colorRelationAmount != undefined &&
              product.colorRelationAmount > 1 && (
                <div className="color-variants">
                  {product.colorRelationAmount +
                    ' ' +
                    translate('product-card.colors')}
                </div>
              )}
          </div>
        </div>
      </div>
      <VariantSelectBuyButtonContainer
        product={product}
        isCategoryPage={true}
        hideText={true}
        dropDownSize={'small'}
      />
      <div className="mr-2 text-gray-600 mt-2 text-sm md:hidden">
        {product.hasVariants ? (
          <span>
            {product.stockStatus.inStock
              ? translate('product-listing.is-in-stock')
              : translate('product-listing.is-not-in-stock')}
          </span>
        ) : (
          product.stockStatus.description
        )}
      </div>
    </div>
  );
};

export default ProductCardLandscape;
